export interface UpsertSessionTransportRequest {
  readonly sessionId: string;
  readonly participantId: string;
  readonly positionSec: number;
  readonly playbackRate: number;
  readonly pitchSemitones: number;
  readonly state: "playing" | "paused" | "stopped";
  readonly commandId?: string;
  readonly seq?: number;
  readonly issuerId?: string;
  readonly issuedAtMono?: number;
  readonly issuedAtEpochMs?: number;
  readonly op?: "transport.update";
  readonly payload?: {
    readonly positionSec: number;
    readonly playbackRate: number;
    readonly pitchSemitones: number;
    readonly state: "playing" | "paused" | "stopped";
  };
}

export interface UpsertSessionTransportResponse {
  readonly sessionId: string;
  readonly positionSec: number;
  readonly playbackRate: number;
  readonly pitchSemitones: number;
  readonly state: "playing" | "paused" | "stopped";
  readonly updatedAtEpochMs: number;
  readonly commandId?: string;
  readonly seq?: number;
  readonly issuerId?: string;
  readonly issuedAtMono?: number;
  readonly issuedAtEpochMs?: number;
  readonly executeAtServerMono?: number;
  readonly executeAtEpochMs?: number;
  readonly op?: "transport.update";
  readonly payload?: {
    readonly positionSec: number;
    readonly playbackRate: number;
    readonly pitchSemitones: number;
    readonly state: "playing" | "paused" | "stopped";
  };
  readonly compatibilityMode?: string;
}

export interface SessionTransportServiceOptions {
  readonly apiBaseUrl: string;
  readonly upsertPath?: string;
  readonly getAuthToken?: (() => Promise<string | undefined>) | undefined;
  readonly onUnauthorized?: (() => Promise<string | undefined>) | undefined;
  readonly fetchImpl?: typeof fetch;
}

const UPSERT_SESSION_TRANSPORT_PATH = "/v2/band-sync/livekit/session-transport/upsert";

export class SessionTransportService {
  private readonly apiBaseUrl: string;
  private readonly upsertPath: string;
  private readonly getAuthToken: (() => Promise<string | undefined>) | undefined;
  private readonly onUnauthorized: (() => Promise<string | undefined>) | undefined;
  private readonly fetchImpl: typeof fetch;

  constructor(options: SessionTransportServiceOptions) {
    this.apiBaseUrl = options.apiBaseUrl;
    this.upsertPath = options.upsertPath ?? UPSERT_SESSION_TRANSPORT_PATH;
    this.getAuthToken = options.getAuthToken;
    this.onUnauthorized = options.onUnauthorized;
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  private buildEndpoint(path: string): string {
    const base = this.apiBaseUrl.trim().replace(/\/$/, "");
    return `${base}${path}`;
  }

  private async postWithAuth(
    endpoint: string,
    payload: object,
    authToken: string | undefined,
  ): Promise<Response> {
    const headers: Record<string, string> = {
      "content-type": "application/json",
    };
    if (authToken) {
      headers.authorization = `Bearer ${authToken}`;
    }
    return this.fetchImpl(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
  }

  async upsertSessionTransport(
    request: UpsertSessionTransportRequest,
  ): Promise<UpsertSessionTransportResponse> {
    const endpoint = this.buildEndpoint(this.upsertPath);
    const initialToken = this.getAuthToken ? await this.getAuthToken() : undefined;
    let response = await this.postWithAuth(endpoint, request, initialToken);
    if (response.status === 401 && this.onUnauthorized) {
      const refreshed = await this.onUnauthorized();
      if (refreshed) {
        response = await this.postWithAuth(endpoint, request, refreshed);
      }
    }
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(
        `upsert session transport failed (${response.status})${body ? ` body=${body}` : ""}`,
      );
    }
    return (await response.json()) as UpsertSessionTransportResponse;
  }
}
